import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { environment } from '../../../../environments/environment';

/**
 * Single source of truth for the multi-tenant runtime context.
 *
 * Store selection is resolved from the URL path segment
 * (e.g. `happyordering.com/abdulordering`) rather than the subdomain.
 *
 * `tenant` comes from the build-time environment file. The active
 * `storeApiKey` is synchronized later from the selected store (or
 * resolved via `/stores/resolve` when it is absent there).
 */
@Injectable({ providedIn: 'root' })
export class HappyTenantContextService {
  private readonly _tenant: WritableSignal<string> = signal<string>('');
  private readonly _subdomain: WritableSignal<string> = signal<string>('');
  private readonly _storeName: WritableSignal<string> = signal<string>('');
  private readonly _storeApiKey: WritableSignal<string | null> =
    signal<string | null>(null);

  readonly tenant: Signal<string> = this._tenant.asReadonly();
  readonly subdomain: Signal<string> = this._subdomain.asReadonly();
  readonly storeName: Signal<string> = this._storeName.asReadonly();
  readonly storeApiKey: Signal<string | null> = this._storeApiKey.asReadonly();

  readonly isReady: Signal<boolean> = computed(
    () => !!this._tenant() && !!this._storeName() && !!this._storeApiKey()
  );

  bootstrap(): void {
    const happyApi: any = (environment as any).happyApi || {};
    const envTenant: string = (happyApi.tenant || '').toString();

    this._tenant.set(envTenant);
    this._subdomain.set('');
    this._storeName.set('');
    this._storeApiKey.set(null);
  }

  setSelectedStore(selection: {
    tenant?: string | null;
    storeName?: string | null;
    storeApiKey?: string | null;
  }): void {
    if (selection.tenant) {
      this._tenant.set(selection.tenant);
    }
    if (selection.storeName !== undefined) {
      this._storeName.set(selection.storeName || '');
    }
    if (selection.storeApiKey !== undefined) {
      this._storeApiKey.set(selection.storeApiKey || null);
    }
  }

  setApiKey(apiKey: string): void {
    if (!apiKey) {
      return;
    }
    this._storeApiKey.set(apiKey);
  }

}
